import { useState, useEffect, useRef, useCallback } from 'react';
import toast from 'react-hot-toast'; // Import toast from react-hot-toast
import { apiUrl } from '../utils/constants.ts'; // Ensure apiUrl is defined correctly here (e.g., 'http://localhost:9001/api')
import { useHistory } from 'react-router-dom'; // <--- Cambiado de useNavigate a useHistory
import { useQueryClient } from '@tanstack/react-query'; // Import useQueryClient

// Define the structure of messages received/sent via WebSocket
interface WebSocketMessage {
    type: string;
    payload: any;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const RECONNECT_INTERVAL_MS = 5000; // 5 seconds

// Determine WebSocket protocol based on apiUrl protocol
const getWebSocketUrl = (httpUrl: string): string => {
    if (!httpUrl) return '';
    const url = new URL(httpUrl);
    const wsProtocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
    // Construct the base WebSocket URL (assuming it's hosted at the root of the same host/port)
    // Adjust '/ws' path if your backend WebSocket server listens on a different path
    return `${wsProtocol}//${url.host}`;
};


export const useWebSocket = (userAddress: string | null | undefined) => {
    const [isConnected, setIsConnected] = useState(false);
    const [lastMessage, setLastMessage] = useState<WebSocketMessage | null>(null);
    const ws = useRef<WebSocket | null>(null);
    const reconnectAttempts = useRef(0);
    const reconnectTimer = useRef<NodeJS.Timeout | null>(null);
    const isUnmounting = useRef(false);
    const connectedForAddress = useRef<string | undefined>(undefined);
    const history = useHistory(); // <--- Cambiado de navigate = useNavigate() a history = useHistory()
    const queryClient = useQueryClient(); // Get query client instance

    const connectWebSocket = useCallback((addressToConnect: string) => {
        if (isUnmounting.current) {
            console.log('[WebSocket] connectWebSocket: Skipping connection: Component is unmounting.');
            return;
        }
        if (ws.current && (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) && connectedForAddress.current === addressToConnect) {
            console.log(`[WebSocket] connectWebSocket: Skipping connection: Already connected/connecting for ${addressToConnect}.`);
            return;
        }
        if (ws.current && connectedForAddress.current && connectedForAddress.current !== addressToConnect) {
            console.log(`[WebSocket] connectWebSocket: Address changed from ${connectedForAddress.current} to ${addressToConnect}. Closing old connection.`);
            ws.current.close(1000, "User address changed");
            ws.current = null;
            setIsConnected(false);
        }

        if (reconnectTimer.current) {
            clearTimeout(reconnectTimer.current);
            reconnectTimer.current = null;
        }

        // Use the helper function to get the base WS URL
        const baseWsUrl = getWebSocketUrl(apiUrl);
        if (!baseWsUrl) {
            console.error("[WebSocket] Cannot connect: apiUrl is not defined or invalid.");
            return;
        }
        const fullWsUrl = `${baseWsUrl}?userAddress=${addressToConnect}`; // Append userAddress query param
        console.log(`[WebSocket] connectWebSocket: Attempting to connect to: ${fullWsUrl}`);

        try {
            ws.current = new WebSocket(fullWsUrl);
            connectedForAddress.current = addressToConnect;

            ws.current.onopen = () => {
                // ... existing onopen logic ...
                if (isUnmounting.current) {
                    console.log('[WebSocket] onopen triggered during unmount, closing socket.');
                    ws.current?.close(1000);
                    return;
                }
                console.log(`[WebSocket] <<< Connection established for ${addressToConnect}. Setting isConnected = true. >>>`);
                setIsConnected(true);
                reconnectAttempts.current = 0;
                if (ws.current?.readyState === WebSocket.OPEN) {
                    ws.current.send(JSON.stringify({ type: 'PING' }));
                }
            };

            ws.current.onclose = (event) => {
                // ... existing onclose logic ...
                if (isUnmounting.current) {
                    console.log(`[WebSocket] onclose triggered during unmount. Code: ${event.code}. No further action.`);
                    setIsConnected(false);
                    ws.current = null;
                    connectedForAddress.current = undefined;
                    return;
                }
                console.warn(`[WebSocket] <<< Connection closed for ${connectedForAddress.current}. Code: ${event.code}, Reason: ${event.reason}. Setting isConnected = false. >>>`);
                setIsConnected(false);
                ws.current = null;
                const closedAddress = connectedForAddress.current;
                connectedForAddress.current = undefined;

                const isCleanClose = event.code === 1000;
                // Adjust reason check if needed based on actual backend close reasons
                const isClosedForNewConnection = event.reason === 'New connection established' || event.reason === 'User address changed' || event.reason === 'Component unmounting' || event.reason === 'User address became invalid';

                // Only reconnect if the closure was unexpected AND it was for the currently active userAddress
                if (!isCleanClose && !isClosedForNewConnection && closedAddress === userAddress && reconnectAttempts.current < MAX_RECONNECT_ATTEMPTS) {
                    reconnectAttempts.current++;
                    console.log(`[WebSocket] Attempting to reconnect for ${userAddress} in ${RECONNECT_INTERVAL_MS / 1000} seconds... (Attempt ${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`);
                    reconnectTimer.current = setTimeout(() => {
                        // Ensure userAddress hasn't changed again before reconnecting
                        if (!isUnmounting.current && userAddress) {
                             connectWebSocket(userAddress);
                        }
                    }, RECONNECT_INTERVAL_MS);
                } else if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
                    console.error(`[WebSocket] Max reconnect attempts (${MAX_RECONNECT_ATTEMPTS}) reached for ${closedAddress}. Stopping reconnection.`);
                } else {
                    console.log(`[WebSocket] Clean close or closed intentionally for ${closedAddress}. No automatic reconnection needed.`);
                }
            };

            ws.current.onerror = (error) => {
                // ... existing onerror logic ...
                 if (isUnmounting.current) {
                    console.log('[WebSocket] onerror triggered during unmount.');
                    setIsConnected(false);
                    ws.current = null;
                    connectedForAddress.current = undefined;
                    return;
                }
                console.error(`[WebSocket] <<< Error for ${connectedForAddress.current}. Setting isConnected = false. >>>`, error);
                setIsConnected(false);
                // Optionally trigger reconnect logic here too, similar to onclose
            };

            ws.current.onmessage = (event) => {
                // ... existing onmessage logic ...
                if (isUnmounting.current) {
                    console.log('[WebSocket] onmessage ignored during unmount.');
                    return;
                }
                try {
                    const message = JSON.parse(event.data as string) as WebSocketMessage;
                    //console.log('[WebSocket] Raw message received:', message); // Log raw message

                    // Handle PONG silently
                    if (message.type === 'PONG') {
                        // console.log('[WebSocket] Received PONG');
                        return; // Don't set PONG as lastMessage
                    }

                    // Set the received message for potential generic handling if needed
                    setLastMessage(message);

                    // Specific message type handling
                    switch (message.type) {
                        case 'PRIVATE_STREAM_REQUEST':
                            console.log('[App WebSocket] Received PRIVATE_STREAM_REQUEST:', message.payload);
                            const { streamerAddress, entryPriceBNB, scheduleInfo, requestId } = message.payload;

                            const notificationText = `Private stream invite from ${streamerAddress.substring(0, 6)}... Price: ${entryPriceBNB} BNB.`;

                            // --- FIX: Use default toast() instead of toast.info() ---
                            toast(notificationText, { // Display only the text
                            // --- END FIX ---
                                icon: '🔔',
                                duration: 15000, // Keep notification longer
                            });
                            // --- END FIX ---

                            // Invalidate queries to update UI lists of requests
                            // The UI component responsible for showing requests should update automatically
                            queryClient.invalidateQueries({ queryKey: ['pendingPrivateRequests', addressToConnect] });
                            queryClient.invalidateQueries({ queryKey: ['privateStreamRequests', addressToConnect] });
                            break;
                        // Add other message type handlers here (e.g., NEW_MESSAGE, MESSAGE_READ_RECEIPT)
                        // case 'NEW_MESSAGE':
                        //     // Handle new chat message notification/update
                        //     break;
                        case 'NEW_MESSAGE':
                        case 'MESSAGES_DELETED':
                        case 'CONVERSATION_CLEARED':
                        case 'USER_BLOCKED':
                        case 'USER_UNBLOCKED':
                            console.log(`[WebSocket] Received ${message.type} message:`, message.payload);
                            setLastMessage(message); // Pass these message types up to the component
                            break;
                        default:
                            console.log('[App WebSocket] Received unhandled message type:', message.type);
                    }

                } catch (error) {
                    console.error('[WebSocket] Failed to parse message or invalid message format:', event.data, error);
                }
            };

        } catch (error) {
            console.error("[WebSocket] Failed to create WebSocket instance:", error);
            setIsConnected(false);
            connectedForAddress.current = undefined;
        }

    }, [apiUrl, userAddress, history, queryClient]); // Add queryClient dependency

    useEffect(() => {
        isUnmounting.current = false;
        const currentAddress = userAddress;

        console.log(`[WebSocket Hook Effect] Running. UserAddress prop: ${currentAddress}, Currently connected for: ${connectedForAddress.current}`);

        if (currentAddress && typeof currentAddress === 'string' && currentAddress.trim() !== '') {
            // Connect if address is valid and not already connected/connecting for this address
            if (!ws.current || connectedForAddress.current !== currentAddress) {
                 console.log(`[WebSocket Hook Effect] Valid address ${currentAddress} detected. Calling connectWebSocket.`);
                 connectWebSocket(currentAddress);
            } else {
                 console.log(`[WebSocket Hook Effect] Valid address ${currentAddress} detected, and already connected/connecting for it. Skipping connectWebSocket call.`);
            }
        } else {
            // Close connection if address becomes invalid/missing
            console.log(`[WebSocket Hook Effect] userAddress is INVALID or missing: ${currentAddress}. Closing any existing connection.`);
            setIsConnected(false);
            if (ws.current) {
                console.log("[WebSocket Hook Effect] Closing existing socket due to invalid userAddress.");
                ws.current.close(1000, "User address became invalid");
                ws.current = null;
            }
            connectedForAddress.current = undefined;
        }

        // Ping interval
        const pingInterval = setInterval(() => {
            if (ws.current && ws.current.readyState === WebSocket.OPEN && !isUnmounting.current) {
                // console.log('[WebSocket] Sending PING'); // Optional: uncomment for debugging
                ws.current.send(JSON.stringify({ type: 'PING' }));
            }
        }, 30000); // 30 seconds

        // Cleanup function
        return () => {
            isUnmounting.current = true;
            console.log(`[WebSocket Hook Cleanup] Running for address prop: ${currentAddress}. Closing connection.`);
            clearInterval(pingInterval);
            if (reconnectTimer.current) {
                clearTimeout(reconnectTimer.current);
                reconnectTimer.current = null;
            }
            if (ws.current) {
                // Remove listeners before closing
                ws.current.onopen = null;
                ws.current.onclose = null; // Prevent onclose logic during cleanup
                ws.current.onerror = null;
                ws.current.onmessage = null;
                if (ws.current.readyState === WebSocket.OPEN || ws.current.readyState === WebSocket.CONNECTING) {
                    console.log('[WebSocket Hook Cleanup] Closing WebSocket connection.');
                    ws.current.close(1000, "Component unmounting");
                }
                ws.current = null;
            }
            // Reset state
            setIsConnected(false);
            setLastMessage(null);
            reconnectAttempts.current = 0;
            connectedForAddress.current = undefined; // Clear connected address on unmount
            console.log('[WebSocket Hook Cleanup] Cleanup complete.');
        };
    }, [userAddress, connectWebSocket]); // connectWebSocket is stable due to useCallback

    const sendMessage = useCallback((message: WebSocketMessage) => {
        // ... existing sendMessage logic ...
        if (ws.current && ws.current.readyState === WebSocket.OPEN) {
            try {
                console.log('[WebSocket] Sending message:', message.type); // Log message type being sent
                ws.current.send(JSON.stringify(message));
                return true;
            } catch (error) {
                console.error('[WebSocket] Failed to send message:', error);
                return false;
            }
        } else {
            console.warn(`[WebSocket] Cannot send message: WebSocket not open. State: ${ws.current?.readyState}`);
            return false;
        }
    }, []); // No dependencies needed if it only uses ws.current

    // Return only the state/functions needed by the consuming component
    // If the consumer needs to react to specific messages beyond notifications,
    // they can use `lastMessage` and useEffect in their component.
    return { isConnected, lastMessage, sendMessage };
};
